import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin, Check, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { getSuggestionsApi } from "../../services/mapApis";

function MapAutoComplete({
  value,
  placeholder,
  changeHandler,
  requireSelection = false,
  isVerified = false,
  onVerifiedChange,
}) {
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [verified, setVerified] = useState(isVerified || false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setInputValue(value || "");
    if (typeof isVerified === "boolean") {
      setVerified(isVerified);
    }
  }, [value, isVerified]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputValue && inputValue.length > 2 && showSuggestions) {
        fetchSuggestions(inputValue);
      } else {
        setSuggestions([]);
      }
    }, 300); // Debounce

    return () => clearTimeout(timeoutId);
  }, [inputValue, showSuggestions]);

  const fetchSuggestions = async (query) => {
    if (!query) return;
    setIsLoading(true);
    try {
      const results = await getSuggestionsApi(query);
      setSuggestions(results);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newVal = e.target.value;
    setInputValue(newVal);
    setVerified(false);
    if (onVerifiedChange) onVerifiedChange(false);
    changeHandler(newVal, false); // Pass unverified flag
    setShowSuggestions(true);
  };

  const handleSelect = (feature) => {
    const name = feature.properties.name;
    const city = feature.properties.city;
    const state = feature.properties.state;
    const country = feature.properties.country;

    // Construct a nice display string
    const parts = [name, city, state, country].filter(Boolean);
    const formattedAddress = parts.join(", ");

    setInputValue(formattedAddress);
    setVerified(true);
    if (onVerifiedChange) onVerifiedChange(true);
    changeHandler(formattedAddress, true); // Pass verified flag
    setShowSuggestions(false);
  };

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div className="relative">
        <Input
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          className={cn(
            "w-full pr-10 transition-all h-11 rounded-xl bg-white border-zinc-200 hover:border-zinc-300 focus-visible:ring-2 focus-visible:ring-amber-500/20 focus-visible:border-amber-500 shadow-2xs text-sm font-medium",
            requireSelection && verified && "border-emerald-500/80 bg-emerald-50/20 focus-visible:ring-emerald-500/20 focus-visible:border-emerald-500",
            requireSelection && !verified && inputValue.length > 2 && "border-amber-400/80"
          )}
        />
        <div className="absolute right-3.5 top-1/2 -translate-y-1/2 flex items-center gap-1 pointer-events-none">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : verified ? (
            <span title="Verified location" className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-600">
              <Check className="h-3.5 w-3.5 stroke-[2.5]" />
            </span>
          ) : (
            <MapPin className="h-4 w-4 text-zinc-400" />
          )}
        </div>
      </div>

      {showSuggestions && (
        <div className="absolute z-50 mt-1.5 w-full overflow-hidden rounded-xl border border-zinc-200/90 bg-white shadow-xl animate-in fade-in-0 zoom-in-95 max-h-60 overflow-y-auto">
          {suggestions.length > 0 ? (
            <>
              {requireSelection && (
                <div className="px-3 py-1.5 text-xs text-amber-700 bg-amber-50 border-b border-amber-200 font-medium flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  Select a recognized place from the list:
                </div>
              )}
              <ul className="divide-y divide-zinc-100">
                {suggestions.map((feature, index) => (
                  <li
                    key={index}
                    onClick={() => handleSelect(feature)}
                    className="flex select-none items-center gap-2.5 px-3 py-2 text-sm outline-none hover:bg-amber-50 hover:text-zinc-900 cursor-pointer transition-colors"
                  >
                    <MapPin className="h-4 w-4 shrink-0 text-amber-500" />
                    <div className="flex flex-col text-left min-w-0">
                      <span className="font-medium text-zinc-900 truncate">
                        {feature.properties.name}
                      </span>
                      <span className="text-xs text-zinc-500 truncate">
                        {[feature.properties.city, feature.properties.state, feature.properties.country]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : inputValue && inputValue.length > 2 && !isLoading ? (
            <div className="px-4 py-3 text-xs text-zinc-500 text-center flex flex-col items-center gap-1">
              <span className="font-medium text-red-500">No matching places found</span>
              <span>Please enter a recognized landmark, area, or campus location.</span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default MapAutoComplete;
